import { Evidently, EvaluateFeatureRequest, VariableValue, Feature } from '@aws-sdk/client-evidently';
import { CognitoIdentityClient, GetOpenIdTokenCommand, GetIdCommand } from '@aws-sdk/client-cognito-identity';
import { STS, AssumeRoleWithWebIdentityCommand } from '@aws-sdk/client-sts';
import { CredentialProvider, Credentials } from '@aws-sdk/types';

import { LocalStorageHelper } from '../../../Shared/helpers/localstoragehelper';
import {
	EvidentlyProviderConfig,
	IEvidentlyProvider,
	IEvidentlyOverrideProvider,
	CaptureMetricArgs,
	EvaluateFeatureArgs,
	FeatureOverrideViewModel,
	EvaluateFeatureValue,
	FeatureEvaluation,
	FeatureOverrideViewModelOption,
} from '../types';

export default class EvidentlyProvider implements IEvidentlyProvider, IEvidentlyOverrideProvider {
	private stsClient: STS;
	private cognitoIdentityClient: CognitoIdentityClient;
	private clientProvider?: Promise<Evidently>;
	private config: EvidentlyProviderConfig;
	private identityIdCacheKey: string;
	private featureEvaluationsCacheKey: string;
	private featureEvaluations: Record<string, FeatureEvaluation>;
	private features: Feature[];
	private credentials: Credentials;
	private storage = LocalStorageHelper.getLocalStorage();
	private activeFeatures = new Set<string>();

	constructor(private queryString = window.location.search) {}

	configure = (config: EvidentlyProviderConfig) => {
		this.config = config;
		this.identityIdCacheKey = `pushpay:evidently:${config.cognito.identityPoolId}:identityId`;
		this.featureEvaluationsCacheKey = `pushpay:evidently:${config.cognito.identityPoolId}:featureEvaluations`;

		const featureEvaluations = this.storage.getItem(this.featureEvaluationsCacheKey);
		this.featureEvaluations = featureEvaluations ? JSON.parse(featureEvaluations) : {};

		this.stsClient = new STS({ region: this.config.cognito.region });
		this.cognitoIdentityClient = new CognitoIdentityClient({ region: this.config.cognito.region });
		this.getEvidentlyClient();
	};

	private getCachedIdentityId = () => {
		return this.storage.getItem(this.identityIdCacheKey) as string;
	};

	private getAndCacheIdentityId = async () => {
		const cachedIdentityId = this.getCachedIdentityId();
		if (cachedIdentityId) {
			return cachedIdentityId;
		}

		const { IdentityId } = await this.cognitoIdentityClient.send(
			new GetIdCommand({
				IdentityPoolId: this.config.cognito.identityPoolId,
			})
		);

		if (IdentityId) {
			this.storage.setItem(this.identityIdCacheKey, IdentityId);
		}

		return IdentityId;
	};

	private credentialsAreValid = () => {
		if (!this.credentials) {
			return false;
		}
		if (!this.credentials.expiration) {
			return true;
		}
		return this.credentials.expiration > new Date(Date.now() + 5 * 60 * 1000);
	};

	private getCredentials: CredentialProvider = async () => {
		if (this.credentialsAreValid()) {
			return this.credentials;
		}

		const openIdTokenResponse = await this.cognitoIdentityClient.send(
			new GetOpenIdTokenCommand({
				IdentityId: this.getCachedIdentityId(),
			})
		);
		const assumeRoleResponse = await this.stsClient.send(
			new AssumeRoleWithWebIdentityCommand({
				RoleArn: this.config.cognito.role,
				RoleSessionName: 'evidently',
				WebIdentityToken: openIdTokenResponse.Token,
			})
		);
		if (!assumeRoleResponse.Credentials) {
			throw new Error('STS response did not contain Credentials.');
		}
		if (!assumeRoleResponse.Credentials.AccessKeyId) {
			throw new Error('STS response did not contain an AccessKeyId');
		}
		if (!assumeRoleResponse.Credentials.SecretAccessKey) {
			throw new Error('STS response did not contain a SecretAccessKey');
		}

		const { AccessKeyId, SecretAccessKey, SessionToken, Expiration } = assumeRoleResponse.Credentials;

		const credentials = {
			accessKeyId: AccessKeyId,
			secretAccessKey: SecretAccessKey,
			sessionToken: SessionToken,
			expiration: Expiration,
		};

		this.credentials = credentials;

		return credentials;
	};

	private buildClient = async () => {
		await this.getAndCacheIdentityId();
		return new Evidently({
			endpoint: this.config.evidently.endpoint,
			region: this.config.evidently.region,
			credentials: this.getCredentials,
		});
	};

	private getEvidentlyClient = () => {
		if (!this.clientProvider) {
			this.clientProvider = this.buildClient();
		}
		return this.clientProvider;
	};

	private setFeatureEvaluation = (featureName: string, value: FeatureEvaluation | 'delete') => {
		if (value === 'delete') {
			delete this.featureEvaluations[featureName];
		} else {
			this.featureEvaluations[featureName] = value;
		}
		this.storage.setItem(this.featureEvaluationsCacheKey, JSON.stringify(this.featureEvaluations));
	};

	setFeatureOverride = (featureName: string, override: FeatureEvaluation) => {
		this.setFeatureEvaluation(featureName, override);
	};

	private getFeatures = async () => {
		if (this.features) {
			return this.features;
		}
		const evidentlyClient = await this.getEvidentlyClient();
		const { features: listFeatures } = await evidentlyClient.listFeatures({
			project: this.config.evidently.project,
		});
		if (!listFeatures) {
			this.features = [];
			return this.features;
		}
		const features = await Promise.all(
			listFeatures
				.filter((f) => f.name)
				.map(async (f) => {
					const { feature } = await evidentlyClient.getFeature({
						project: this.config.evidently.project,
						feature: f.name,
					});
					return feature;
				})
		);
		this.features = features.filter(Boolean) as Feature[];
		return this.features;
	};

	getFeatureOverridesViewModel = async (): Promise<FeatureOverrideViewModel[]> => {
		const features = await this.getFeatures();
		return features
			.map(({ name: featureName, variations, valueType }) => {
				if (!featureName || !valueType || !variations || !variations.length) {
					return;
				}
				return {
					featureName,
					options: [
						{
							name: 'No override',
							value: '' as string | number | boolean,
							selected: Boolean(
								!this.featureEvaluations[featureName] ||
									!variations
										.map(({ name }) => name)
										.find((x) => x === this.featureEvaluations[featureName].identifier)
							),
							onSelected: () => {
								this.setFeatureEvaluation(featureName, 'delete');
								return this.getFeatureOverridesViewModel();
							},
						},
						...(variations
							.map(({ name, value: variationValue }) => {
								if (!name || !variationValue) {
									return;
								}
								const value = (variationValue as Record<string, any>)[
									`${valueType.toLowerCase()}Value`
								];
								return {
									name,
									value,
									selected: Boolean(
										this.featureEvaluations[featureName] &&
											this.featureEvaluations[featureName].identifier === name &&
											this.featureEvaluations[featureName].variationValue === value
									),
									onSelected: () => {
										this.setFeatureEvaluation(featureName, {
											identifier: name,
											variationValue: value,
										});
										return this.getFeatureOverridesViewModel();
									},
								};
							})
							.filter(Boolean) as unknown as FeatureOverrideViewModelOption[]),
					],
				};
			})
			.filter(Boolean) as unknown as FeatureOverrideViewModel[];
	};

	reset = (): Promise<FeatureOverrideViewModel[]> | undefined => {
		this.storage.removeItem(this.identityIdCacheKey);
		this.storage.removeItem(this.featureEvaluationsCacheKey);
		this.clientProvider = undefined;
		this.featureEvaluations = {};
		return this.features ? this.getFeatureOverridesViewModel() : undefined;
	};

	private getIdentityIdOverrideFromUrl = (featureName: string) => {
		return typeof URLSearchParams !== 'undefined' && new URLSearchParams(this.queryString).get(featureName);
	};

	evaluateFeature = async <T extends 'bool' | 'string' | 'long' | 'double'>(
		args: EvaluateFeatureArgs<T>
	): Promise<EvaluateFeatureValue<T>> => {
		const { featureName, variationType, defaultVariation, evaluationContext, timeout = 2000 } = args;

		try {
			const identityOverrideFromUrl = this.getIdentityIdOverrideFromUrl(featureName);

			if (!identityOverrideFromUrl && this.featureEvaluations[featureName]) {
				return this.featureEvaluations[featureName].variationValue as EvaluateFeatureValue<T>;
			}

			const makeEvaluateFeatureRequest = async () => {
				const evidentlyClient = await this.getEvidentlyClient();
				const entityId = this.getIdentityIdOverrideFromUrl(featureName) || this.getCachedIdentityId();
				const evaluateFeatureRequest: EvaluateFeatureRequest = {
					entityId,
					feature: featureName,
					project: this.config.evidently.project,
					evaluationContext: JSON.stringify(evaluationContext),
				};
				const result = await evidentlyClient.evaluateFeature(evaluateFeatureRequest);
				if (result.value && result.value[`${variationType}Value` as keyof VariableValue]) {
					return {
						identifier: entityId,
						variationValue: result.value[`${variationType}Value` as keyof VariableValue],
					} as { identifier: string; variationValue: EvaluateFeatureValue<T> };
				}
				throw new Error(
					`Evidently Feature Evaluation of ${featureName} did not return a valid value for ${variationType}Value`
				);
			};

			const timeoutFunc = () =>
				new Promise<never>((_, reject) => {
					window.setTimeout(
						() =>
							reject(
								new Error(`Evidently Feature Evaluation of ${featureName} timed out after ${timeout}ms`)
							),
						timeout
					);
				});

			const featureEvaluationResult = await Promise.race([
				makeEvaluateFeatureRequest(),
				// we will use overrides set by querystring in e2e tests to stabilize the A/B test evaluation
				// we want to hit the real Evidently service but we should not apply the timeout to the request
				...(identityOverrideFromUrl ? [] : timeoutFunc()),
			]);

			this.setFeatureEvaluation(featureName, featureEvaluationResult);
			return featureEvaluationResult.variationValue;
		} catch (error) {
			rg4js('send', { error, tags: ['Evidently'] });
			this.setFeatureEvaluation(featureName, {
				identifier: defaultVariation.name,
				variationValue: defaultVariation.value,
			});
			return defaultVariation.value;
		} finally {
			this.activeFeatures.add(featureName);
		}
	};

	captureMetric = async (args: CaptureMetricArgs) => {
		try {
			const { featureName, metrics, entityIdKey = 'entityId' } = args;
			if (!this.activeFeatures.has(featureName)) {
				return;
			}

			const evidentlyClient = await this.getEvidentlyClient();
			const entityId = this.featureEvaluations[featureName] && this.featureEvaluations[featureName].identifier;
			if (!entityId) {
				return;
			}

			const metricData = {
				[entityIdKey]: entityId,
				...metrics,
			};

			const response = await evidentlyClient.putProjectEvents({
				project: this.config.evidently.project,
				events: [
					{
						timestamp: new Date(),
						type: 'aws.evidently.custom',
						data: JSON.stringify(metricData),
					},
				],
			});
			return response;
		} catch (error) {
			rg4js('send', { error, tags: ['Evidently'] });
		}
	};
}
